<template>
    <div v-if="page && (node || wire)" class="space-y-2 p-1 text-sm text-white">
        <header class="flex items-center justify-between gap-2 rounded px-2 py-1" :style="{ background: view.color }">
            <span class="truncate font-semibold">{{ view.icon }} {{ view.title }}</span>
            <form-button color="danger" width="auto" height="24px"
                :title="node ? 'Delete this node and its wires' : 'Delete this wire'"
                @click="hass.deleteSelection(page)">Delete</form-button>
        </header>
        <ul v-if="diagnostics.length" class="space-y-0.5">
            <li v-for="item in diagnostics" :key="item.code + item.message" class="text-xs italic"
                :class="item.severity === 'error' ? 'text-red-300' : 'text-amber-300'">{{ item.message }}</li>
        </ul>

        <HassEntityProperties v-if="node?.kind === 'entity'" :page="page" :graph="graph" :node="node" :context="context" :settings="settings" />
        <HassBindingProperties v-else-if="node?.kind === 'binding'" :key="node.id"
            :page="page" :graph="graph" :node="node" :context="context" :settings="settings" />
        <HassSwitchProperties v-else-if="node?.kind === 'switch'" :key="node.id"
            :page="page" :graph="graph" :node="node" :context="context" :settings="settings" />

        <HassTriggerProperties v-else-if="node?.kind === 'trigger'" :page="page" :graph="graph" :node="node" :context="context" />
        <HassConditionProperties v-else-if="node?.kind === 'condition'" :key="node.id"
            :page="page" :graph="graph" :node="node" :context="context" :settings="settings" />
        <HassActionProperties v-else-if="node?.kind === 'action'" :key="node.id"
            :page="page" :graph="graph" :node="node" :context="context" :settings="settings" />

        <section v-if="wire" class="space-y-1 text-xs text-gray-200">
            <p><span class="text-gray-400">From</span> {{ endpointLabel(wire.from) }}</p>
            <p><span class="text-gray-400">To</span> {{ endpointLabel(wire.to) }}</p>
        </section>
    </div>
</template>

<script setup>
import { computed, toRaw } from 'vue';
import { useHassFlowStore } from '../../stores/hassFlowStore.js';
import { useUndoStore } from '../../history/undoStore.js';
import { findNode, findWire } from '../../graph/graphModel.js';
import { validateHassGraph } from '../../hass/hassGraph.js';
import { createPageHassContext } from '../../hass/pageHass.js';
import { describeHassNode } from '../../hass/hassPresentation.js';
import HassEntityProperties from './HassEntityProperties.vue';
import HassBindingProperties from './HassBindingProperties.vue';
import HassSwitchProperties from './HassSwitchProperties.vue';
import HassTriggerProperties from './HassTriggerProperties.vue';
import HassConditionProperties from './HassConditionProperties.vue';
import HassActionProperties from './HassActionProperties.vue';

/** Properties of the selected Home Assistant flow node or wire. */
const props = defineProps({
    layer: { type: Object, default: null },
    settings: { type: Object, default: () => ({}) },
});
const hass = useHassFlowStore();
const undo = useUndoStore();

// page.hass is plain data outside Vue; these counters say when it changed.
// The page is unwrapped so the graph handed to children stays plain data.
const revision = computed(() => `${hass.revision}|${undo.historyVersion}`);
const page = computed(() => {
    revision.value;
    const selection = hass.selection;
    return selection ? toRaw(props.layer)?.findOne?.('#' + selection.pageUuid) ?? null : null;
});
const graph = computed(() => {
    revision.value;
    return page.value?.hass ?? null;
});
const context = computed(() => (page.value ? createPageHassContext(page.value) : { getObject: () => null }));
const node = computed(() => hass.selection?.kind === 'node' && graph.value ? findNode(graph.value, hass.selection.id) : null);
const wire = computed(() => hass.selection?.kind === 'wire' && graph.value ? findWire(graph.value, hass.selection.id) : null);
const view = computed(() => (node.value ? describeHassNode(node.value) : { icon: '〰', title: 'Wire', color: '#475569' }));
const diagnostics = computed(() => (node.value && graph.value
    ? validateHassGraph(graph.value, context.value).filter(item => item.nodeId === node.value.id)
    : []));

function endpointLabel(endpoint) {
    if (endpoint.object !== undefined) {
        const object = context.value.getObject(endpoint.object);
        return object ? `${object.obj} p${page.value.haspid}b${object.haspid}` : 'missing object';
    }
    const target = findNode(graph.value, endpoint.node);
    return target ? `${describeHassNode(target).title} (${endpoint.port})` : 'missing node';
}
</script>
